import { AlertMessage, ErrorMessage, ProgressIndicator, Spinner } from "../../../../widgets"
import { AttributeDataType, IServiceMetadata, IServiceReqApprovalModel, IServiceRequest, ServiceRequestStatus } from "../models"
import { FC, createContext, useContext, useEffect, useState } from "react"
import { FetchResult, FetchStatus } from "../../../../base/types"
import { IServiceRequestState, useServiceRequestFetchApi } from "./useServiceRequestFetchApi"
import { useCreateAndSubmitServiceRequestMutation, useCreateServiceRequestMutation, useExtendGatePassServiceReqMutation, useMultipleDayGatePassServiceReqCreationMutation, useResendApprovalRequestMutation, useResetRequestStatusToDraftMutation, useSingleDayGatePassServiceReqCreationMutation, useUpdateApprovalStatusMutation, useUpdateServiceRequestMutation } from "../services"

import { useAuth } from "../../../../../modules/auth"
import { useSearchParams } from "react-router-dom"
import { useDataContext } from "../../../page/context"

interface IServiceRequestContext extends FetchResult<IServiceRequestState> {
    entity?: string,
    refetch: () => void,
    updateApprovalStatus: (request: IServiceReqApprovalModel) => Promise<void>,
    updateServiceRequest: (request: Partial<IServiceRequest>) => Promise<IServiceRequest>,
    updateExtendServiceRequest: (requestId: string) => Promise<string>,
    createServiceRequest: (request: Partial<IServiceRequest>) => Promise<IServiceRequest>,
    createAndSubmitServiceRequest: (request: Partial<IServiceRequest>) => Promise<IServiceRequest>,
    resendApprovalRequest: (requestId: string) => Promise<void>,
    setErrorMessage: (message: string) => void,
    resetRequestStatusToDraft: (requestId: string) => Promise<void>,
}

const context = createContext<IServiceRequestContext>({
    status: FetchStatus.Idle,
    refetch: () => { },
    updateServiceRequest: () => Promise.reject(),
    updateExtendServiceRequest: () => Promise.reject(),
    createServiceRequest: () => Promise.reject(),
    createAndSubmitServiceRequest: () => Promise.reject(),
    updateApprovalStatus: () => Promise.reject(),
    resendApprovalRequest: () => Promise.reject(),
    resetRequestStatusToDraft: () => Promise.reject(),
    setErrorMessage: () => { }
})

export function useServiceRequestContext() {
    return useContext(context)
}

interface IServiceRequestProviderProps {
    entity?: string
    requestId?: string
    serviceId?: string
    enableApprovalSMS?: boolean,
    children?: React.ReactNode
}

export const ServiceRequestProvider: FC<IServiceRequestProviderProps> = ({
    requestId,
    serviceId,
    entity,
    enableApprovalSMS,
    children
}) => {
    const [createRequest] = useCreateServiceRequestMutation()
    const [updateRequest] = useUpdateServiceRequestMutation()
    const [createAndSubmit] = useCreateAndSubmitServiceRequestMutation()

    const [updateStatus] = useUpdateApprovalStatusMutation()
    const [resendRequest] = useResendApprovalRequestMutation()
    // const [DeleteRequest] = useDeleteRequestMutation()
    const [MultipleDayGatePassServiceReq] = useMultipleDayGatePassServiceReqCreationMutation()
    const [SingleDayGatePassServiceReq] = useSingleDayGatePassServiceReqCreationMutation()
    const [ExtendGatePassServiceReq] = useExtendGatePassServiceReqMutation()
    const [resetToDraft] = useResetRequestStatusToDraftMutation()

    const { getContextData, refetchRequest } = useServiceRequestFetchApi({ requestId, serviceId })
    const { user } = useAuth()
    const [, setSearchParams] = useSearchParams();
    const [errorMessage, setErrorMessage] = useState('')
    const [errorMessage1, setErrorMessage1] = useState('')
    const [loading, setLoading] = useState(false)
    const { refetch: refetchData } = useDataContext()

    const [state, setState] = useState<FetchResult<IServiceRequestState>>({ status: FetchStatus.Idle })

    useEffect(() => {
        if (!serviceId && !requestId)
            return;

        setState({ status: FetchStatus.Loading })
        getContextData()
            .then(result => {
                if (
                    !result ||
                    !result.metadata ||
                    !Array.isArray(result.metadata.stages) || result.metadata.stages.length === 0 ||
                    !Array.isArray(result.metadata.attributes) || result.metadata.attributes.length === 0 ||
                    !Array.isArray(result.metadata.serviceAttributes) || result.metadata.serviceAttributes.length === 0
                ) {
                    throw new Error("This Service is Currently Unavailable");
                }

                setState({ result, status: FetchStatus.Success });
            })
            .catch(error => {
                const message = error.message || "Failed to fetch service request metadata.";
                setErrorMessage1(message);
                setState({ status: FetchStatus.Error, error });
            });

        // eslint-disable-next-line
    }, [requestId, serviceId])

    useEffect(() => {
        if (state?.status !== FetchStatus.Success ||
            !state.result?.metadata ||
            state.result?.serviceRequest)
            return;

        const { service, stages } = state.result.metadata
        if ((!service || service.paidService !== true) && stages.length > 1) {
            return;
        }

        createPaidServiceRequest(state.result.metadata)

        // eslint-disable-next-line
    }, [state])

    const createPaidServiceRequest = (metadata: IServiceMetadata) => {
        const { service, attributes, stages } = metadata

        if ((!service || !service.paidService === true) && stages[0].stageNumber > 1) {
            return;
        }

        const attr = attributes.find(p =>
            (p.datatype === AttributeDataType.Money) ||
            p.datatype === AttributeDataType.DocumentChecklist ||
            p.datatype === AttributeDataType.MultitypeDocument)

        if (!attr) return;

        const currentStageId = metadata.stages[0].id;
        const requestorId = user?.contactId;

        let request: Partial<IServiceRequest> = {
            serviceId: service.id,
            stageId: currentStageId,
            name: service.name,
            requesterId: requestorId
        }

        createServiceRequest(request)
            .then((request) => setSearchParams({ "id": request.id }))
    }

    const createServiceRequest = (request: Partial<IServiceRequest>) => {
        setErrorMessage('')
        return createRequest(request)
            .unwrap()
            .then(data => {
                setState({ ...state, result: { ...state.result, serviceRequest: data } })
                return Promise.resolve(data)
            })
            .catch(error => {
                const message = error.data.message || 'Failed to create the request'
                setErrorMessage(message)
                return Promise.reject(message)
            })
    }

    const createAndSubmitServiceRequest = (request: Partial<IServiceRequest>): Promise<IServiceRequest> => {
        setErrorMessage('')
        return createAndSubmit(request)
            .unwrap()
            .then(data => {
                const { isSubmitted, isCreated, serviceRequest, message } = data

                if (!serviceRequest)
                    return Promise.reject({ data: { message } })

                if (isSubmitted === true) {
                    setState({ ...state, result: { ...state.result, serviceRequest } })

                    if (enableApprovalSMS)
                        return resendRequest(serviceRequest?.id!)
                            .then(() => Promise.resolve(serviceRequest!))
                } else if (isCreated === true) {
                    setState({ ...state, result: { ...state.result, serviceRequest } })
                    setErrorMessage(message || 'Failed to submit the request');
                }

                return Promise.resolve(serviceRequest!)
            })
            .catch(error => {
                const message = error.data.message || 'Failed to create the request'
                setErrorMessage(message)
                return Promise.reject(message)
            })
    }

    const updateServiceRequest = (request: Partial<IServiceRequest>) => {
        setErrorMessage('')
        return updateRequest(request)
            .unwrap()
            .then(data => {
                setState({ ...state, result: { ...state.result, serviceRequest: data } })
                if ((enableApprovalSMS || data.multiDayGatePass) && data.statusCode === ServiceRequestStatus.Submitted)
                    return resendRequest(data.id)
                        .then(() => Promise.resolve(data))
                return Promise.resolve(data)
            })
            .catch((error) => {
                const message = error.data.message || 'Failed to update the request'
                setErrorMessage(message)
                return Promise.reject(message)
            })
    }

    const updateExtendServiceRequest = (requestId: string) => {
        setErrorMessage('')
        return ExtendGatePassServiceReq(requestId)
            .unwrap()
            .then((data) => {
                return Promise.resolve(data.message)
            })
            .catch(error => {
                setErrorMessage(error.data.message || 'An error occurred while processing your request.')
                return Promise.reject(error)
            })
    }

    const updateApprovalStatus = (request: IServiceReqApprovalModel) => {
        setErrorMessage('')
        return updateStatus(request)
            .unwrap()
            .then(() => Promise.resolve())
            .catch((error) => {
                const message = error.data.message || 'Failed to update the request'
                setErrorMessage(message)
                return Promise.reject(message)
            })
    }

    const resendApprovalRequest = (requestId: string) => {
        setErrorMessage('')
        return resendRequest(requestId)
            .unwrap()
            .then(() => Promise.resolve())
            .catch(error => {
                setErrorMessage(error.data.message || 'An error occurred while processing your request.')
                return Promise.reject(error)
            })
    }

    const resetRequestStatusToDraft = (requestId: string) => {
        setErrorMessage('')
        return resetToDraft(requestId)
            .unwrap()
            .then(() => Promise.resolve())
            .catch(error => {
                setErrorMessage(error.data.message || 'An error occurred while processing your request.')
                return Promise.reject(error)
            })
    }

    const refetch = () => {
        refetchRequest()
            .then(data => {
                setState({ ...state, result: { ...state.result, serviceRequest: data } })
                return Promise.resolve(data)
            })
            .catch((error) => {
                const message = error.data.message || 'Failed to fetch the request'
                setErrorMessage(message)
                return Promise.reject(message)
            })
    }

    const handleClick = (requestId: string) => {
        setLoading(true)
        return MultipleDayGatePassServiceReq(requestId)
            .unwrap()
            .then((id) => {
                // window.location.href = "/site(4d100008-7828-ef11-840a-000d3af278af)/page(c325ab80-6ab7-11ef-8876-1bd287fefa34)?mode=View";
                refetchData()
                Promise.resolve()
            })
            .catch(error => {
                setErrorMessage(error.data.message || 'An error occurred while processing your request.')
                return Promise.reject(error)
            }).finally(() => setLoading(false))
    };

    const handleSingleDayPassClick = (requestId: string) => {
        setLoading(true)
        return SingleDayGatePassServiceReq(requestId)
            .unwrap()
            .then((id) => {
                // window.location.href = "/site(4d100008-7828-ef11-840a-000d3af278af)/page(c325ab80-6ab7-11ef-8876-1bd287fefa34)?mode=View";
                refetchData()
                Promise.resolve()
            })
            .catch(error => {
                setErrorMessage(error.data.message || 'An error occurred while processing your request.')
                return Promise.reject(error)
            }).finally(() => setLoading(false))
    };

    return <context.Provider value={{
        ...state,
        entity,
        refetch,
        updateServiceRequest,
        updateExtendServiceRequest,
        createServiceRequest,
        updateApprovalStatus,
        resendApprovalRequest,
        resetRequestStatusToDraft,
        createAndSubmitServiceRequest,
        setErrorMessage
    }}>
        <ProgressIndicator show={state.status === FetchStatus.Loading} message="Loading..." />
        {errorMessage1 ?
            <ErrorMessage className="my-3 fs-8" show={!!errorMessage1} message={errorMessage1} /> :
            <AlertMessage type="Error" show={state.status === FetchStatus.Error} message="Failed to load service request details." />
        }

        {children}

        {errorMessage === "Kindly proceed with the link for multiple day gate pass:" ? (
            <div className="mb-2 text-danger">
                {/* Display message followed by the link */}
                Kindly proceed with the link for multiple day gate pass:{" "}
                <button
                    className="btn btn-link text-primary fs-6 fw-bold mx-1"
                    onClick={() => handleClick(requestId || '')}>
                    {loading && <Spinner show={loading} />}
                    Click here
                </button>
            </div>
        ) :
            state.result?.serviceRequest?.statusCode === ServiceRequestStatus.Draft &&
                state.result?.serviceRequest?.multiDayGatePass ? (<>
                    <div className="mb-2 text-danger">
                        {/* Display message followed by the link */}
                        Kindly proceed with the link for single day gate pass:{" "}
                        <button
                            className="btn btn-link text-primary fs-6 fw-bold mx-1"
                            onClick={() => handleSingleDayPassClick(requestId || '')}>
                            {loading && <Spinner show={loading} />}
                            Click here
                        </button>
                    </div>
                </>) :
                (
                    <></>
                )}
        {
            (errorMessage && errorMessage !== "Kindly proceed with the link for multiple day gate pass:") && <ErrorMessage className="my-3 fs-8" show={!!errorMessage} message={errorMessage} />
        }
    </context.Provider>
}